/**
 * BuffetRulesModal — mandatory buffet waste-rules notice (Spanish public text).
 * Shown once per table session, before the first buffet order. The primary
 * button stays disabled until the customer checks the acceptance box.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const RULES = [
  "Cada pieza de sushi que quede sin consumir tendrá un coste adicional de 2 € por pieza.",
  "No está permitido comer únicamente el pescado o la cobertura y dejar el arroz. Estas piezas también se considerarán sobrantes y tendrán un coste adicional de 2 € por pieza.",
  "Las hamburguesas de sushi, los bocadillos de sushi y los temakis son piezas de mayor tamaño y contienen una mayor cantidad de ingredientes. Cada unidad que quede sin consumir tendrá un coste adicional de 5 €.",
  "Las piezas sobrantes podrán llevarse a casa después de abonar el importe correspondiente por cada pieza.",
  "Para evitar desperdicios, recomendamos realizar pedidos pequeños y repetir todas las veces que sea necesario.",
];

export function BuffetRulesModal({ open, onAccept, onBack, isLoading, themeColor = "#f97316" }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onBack?.(); }}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-1.5rem)] bg-white dark:bg-zinc-900 p-0 overflow-hidden flex flex-col max-h-[92vh]"
        data-testid="buffet-rules-modal"
      >
        {/* Header */}
        <div className="px-5 py-4 sm:px-6 sm:py-5" style={{ backgroundColor: themeColor }}>
          <DialogTitle className="text-white font-black text-xl sm:text-2xl leading-snug tracking-wide">
            NORMAS DEL BUFFET
          </DialogTitle>
          <p className="text-white/90 font-semibold text-sm sm:text-base mt-1">
            LEE Y ACEPTA ANTES DE EMPEZAR
          </p>
        </div>

        {/* Full rules — scrollable, large readable type */}
        <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6 space-y-4">
          <p className="text-base sm:text-lg text-gray-800 dark:text-gray-100 font-medium leading-relaxed">
            Nuestro buffet está pensado para que disfrutes de toda la experiencia Kaiso,
            pidiendo únicamente lo que realmente vas a consumir.
          </p>
          <ol className="space-y-3">
            {RULES.map((rule, idx) => (
              <li key={idx} className="flex gap-3" data-testid={`buffet-rule-${idx + 1}`}>
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full text-white font-bold text-base flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: themeColor }}
                >
                  {idx + 1}
                </span>
                <p className="text-base sm:text-lg text-gray-800 dark:text-gray-100 leading-relaxed">
                  {rule}
                </p>
              </li>
            ))}
          </ol>
        </div>

        {/* Acceptance + actions */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4 sm:px-6 space-y-3 bg-gray-50 dark:bg-zinc-800">
          <label
            className="flex items-start gap-3 cursor-pointer select-none"
            data-testid="buffet-rules-accept-label"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 w-6 h-6 flex-shrink-0 accent-current cursor-pointer"
              style={{ accentColor: themeColor }}
              data-testid="buffet-rules-checkbox"
            />
            <span className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100 leading-snug">
              He leído y acepto las normas del buffet.
            </span>
          </label>

          <Button
            type="button"
            disabled={!checked || isLoading}
            onClick={onAccept}
            className="w-full h-14 text-white font-black text-base sm:text-lg rounded-xl tracking-wide disabled:opacity-40"
            style={{ backgroundColor: themeColor }}
            data-testid="buffet-rules-accept-btn"
          >
            {isLoading
              ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Guardando...</>
              : "ACEPTAR Y EMPEZAR EL PEDIDO"}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="w-full h-11 text-base font-semibold rounded-xl border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
            data-testid="buffet-rules-back-btn"
          >
            VOLVER
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
